import { useMemo } from 'react'
import { Alert, Col, Container, Row, Spinner, Table } from 'react-bootstrap'
import QuickForm from '../../Components/Forms/QuickForm/QuickForm'
import {
    useGetBookingsQuery,
    useGetTurnoversQuery,
    useGetUserResourceAccessPropertiesQuery,
} from '../../services/bClientApi'
import useUserRefs from '../../Hooks/UseUsersQuery/UseUserRefs'
import { TurnoverType } from '../../Types/turnoverTypes'
import { PropertyBookingEntry } from '../../Types/bookingTypes'
import timeService from '../../services/time/timeService'
import useMyProperties from '../../Hooks/UseMyProperties/UseMyPropertiesHook'
import { hasChildren } from '../../Helpers/Property/hasChildren'
import { Link } from 'react-router-dom'
import useActiveQueryHook from '../../Hooks/UseActiveQuery/useActiveQueryHook'
import { convertActiveQueryToUrl } from '../../Helpers/Query/queryHelpers'
import { navigateTo } from '../../redux/router/routerActions'
import { useAppDispatch } from '../../redux/store'

export default function OwnerTurnoversPage() {

    const dispatch = useAppDispatch()
    const { myProperties } = useMyProperties()

    const { activeQuery } = useActiveQueryHook({ query: {} })

    const filters = {
        ownerId: activeQuery.query.ownerId || '',
        startDate: activeQuery.query.startDate as number | undefined,
        endDate: activeQuery.query.endDate as number | undefined,
    }

    const hasSubmitted = !!activeQuery.query.ownerId

    const userRefs = useUserRefs()

    const ownerOptions = useMemo(() => {
        const seen = new Set<string>()
        const options: { value: string; text: string }[] = []
        for (const key in userRefs) {
            const user = userRefs[key]
            if (user.role !== 'owner') continue
            const val = user._id
            if (seen.has(val)) continue
            seen.add(val)
            options.push({ value: val, text: `${user.firstName} ${user.lastName}` })
        }
        return [
            { value: 'allOwners', text: '👥 All Owners' },
            ...options.sort((a, b) => a.text.localeCompare(b.text))]
    }, [userRefs])

    const {
        data: ownerPropertiesData,
        isLoading: propertiesLoading
    } = useGetUserResourceAccessPropertiesQuery(
        {
            userId: filters.ownerId === 'allOwners' ? ownerOptions.map((option: { value: string; text: string }) => option.value) : filters.ownerId,
            pagination: { page: 1, limit: 150, all: true },
            allResourceData: false,
        },
        { skip: !filters.ownerId }
    )

    const ownerPropertyIds: string[] = !ownerPropertiesData?.docs ? [] : ownerPropertiesData.docs.map((p: any) => p.resourceId).filter((id: string) => {
        // check if property is in myProperties and only include if it does not have children
        const prop = myProperties[id]
        if (!prop) return false
        return !hasChildren(prop)
    })

    const shouldSkipTurnovers =
        !filters.ownerId ||
        !filters.startDate ||
        !filters.endDate ||
        ownerPropertyIds.length === 0 ||
        propertiesLoading

    const {
        data: turnoversData,
        isLoading: turnoversLoading
    } = useGetTurnoversQuery(
        {
            turnoversQuery: {
                propertyId: ownerPropertyIds,
                scheduledForStart: filters.startDate,
                scheduledForEnd: filters.endDate,
                status: 'ready',
            },
            pagination: { page: 1, limit: 500, all: true },
        },
        { skip: shouldSkipTurnovers }
    )

    const turnovers: TurnoverType[] = turnoversData?.docs || []

    const turnoverIds: string[] = turnovers.map((t) => t._id as string)

    const {
        data: bookingsData,
        isLoading: bookingsLoading
    } = useGetBookingsQuery(
        {
            bookingsQuery: {
                // turnoverId: turnoverIds,
                ...(filters.ownerId !== 'allOwners' ? { ownerId: filters.ownerId } : { propertyId: ownerPropertyIds }),
                source: ['owner', 'owner-guest'],
            },
            pagination: { page: 1, limit: 500, all: true },
        },
        { skip: turnoverIds.length === 0 }
    )

    const ownerBookings: PropertyBookingEntry[] = bookingsData?.docs || []

    const bookingByTurnoverId: { [key: string]: PropertyBookingEntry } = {}

    ownerBookings.forEach((b) => {
        if (b.turnoverId) bookingByTurnoverId[b.turnoverId] = b
    })


    const billedTurnovers = turnovers.filter((t) => t._id && bookingByTurnoverId[t._id])

    const isLoading =
        (filters.ownerId && propertiesLoading) ||
        turnoversLoading ||
        (turnoverIds.length > 0 && bookingsLoading)

    const handleSubmit = (values: any) => {
        const startOfDay = timeService.createLuxonDate(values.startDate).startOf('day').toMillis()
        const endOfDay = timeService.createLuxonDate(values.endDate).endOf('day').toMillis()
        const newQuery = {
            ownerId: values.ownerId,
            startDate: startOfDay,
            endDate: endOfDay,
        }
        const destination = `/owner-turnovers?${convertActiveQueryToUrl({ query: newQuery, page: 1, limit: activeQuery.limit })}`
        dispatch(navigateTo(destination))
    }

    const ownerHasNoProperties =
        hasSubmitted &&
        filters.ownerId &&
        !propertiesLoading &&
        ownerPropertyIds.length === 0

    return (
        <Container>
            <Row>
                <Col>
                    <h1>Owner Turnovers</h1>
                    <p className="text-muted">
                        View ready turnovers for owner-booked stays within a date range.
                    </p>
                    <hr />
                    <QuickForm
                        layout={{ xs: 1, md: 3 }}
                        initialData={{
                            ownerId: filters.ownerId,
                            startDate: filters.startDate,
                            endDate: filters.endDate,
                        }}
                        fields={[
                            {
                                fieldName: 'ownerId',
                                fieldLabel: 'Owner',
                                fieldType: 'select',
                                placeholder: '- Select Owner -',
                                options: ownerOptions,
                            },
                            {
                                fieldName: 'startDate',
                                fieldLabel: 'Start Date',
                                fieldType: 'date',
                                placeholder: 'Start Date',
                            },
                            {
                                fieldName: 'endDate',
                                fieldLabel: 'End Date',
                                fieldType: 'date',
                                placeholder: 'End Date',
                            },
                        ]}
                        onSubmit={handleSubmit}
                        submitText="Search"
                    />
                    <hr />

                    {isLoading && (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                        </div>
                    )}

                    {!isLoading && ownerHasNoProperties && (
                        <Alert variant="warning">
                            This owner has no properties assigned.
                        </Alert>
                    )}

                    {!isLoading && hasSubmitted && !ownerHasNoProperties && filters.ownerId && filters.startDate && filters.endDate && (
                        <>
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <h5 className="mb-0">
                                    Owner Bookings with Ready Turnovers:{' '}
                                    <strong>{billedTurnovers.length}</strong>
                                </h5>
                                {turnovers.length > 0 && (
                                    <span className="text-muted">
                                        ({turnovers.length} ready turnovers found for owner properties)
                                    </span>
                                )}
                            </div>

                            {billedTurnovers.length === 0 ? (
                                <Alert variant="info">
                                    No owner-booked turnovers found for the selected criteria.
                                </Alert>
                            ) : (
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Property</th>
                                            <th>Guest</th>
                                            <th>Check In</th>
                                            <th>Check Out</th>
                                            <th>Cleaning</th>
                                            <th>Management</th>
                                            <th>Total</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {billedTurnovers.map((turnover) => {
                                            const booking = bookingByTurnoverId[turnover._id!]
                                            const cleaning = booking?.cleaning ?? myProperties[turnover.propertyId].pricing.cleaning ?? 0
                                            const management = booking?.source !== 'owner' ? 125 : 0
                                            const total = cleaning + management
                                            return (
                                                <tr key={turnover._id}>
                                                    <td>
                                                        {myProperties[turnover.propertyId].title?.split(' - ')[0] ||
                                                            turnover.propertyId}
                                                    </td>
                                                    <td>
                                                        {booking?.contactInfo?.firstName} {booking?.contactInfo?.lastName}
                                                    </td>
                                                    <td>
                                                        {booking?.checkIn
                                                            ? timeService.getFormattedTime(booking.checkIn, 'MM/dd/yyyy')
                                                            : '—'}
                                                    </td>
                                                    <td>
                                                        {booking?.checkOut
                                                            ? timeService.getFormattedTime(booking.checkOut, 'MM/dd/yyyy')
                                                            : '—'}
                                                    </td>
                                                    <td>${cleaning}</td>
                                                    <td>${management}</td>
                                                    <td>${total}</td>
                                                    <td>
                                                        <Link to={`/booking/${booking?._id}`} className="btn btn-sm btn-secondary">
                                                            👀
                                                        </Link>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={4}><strong>Totals</strong></td>
                                            <td><strong>${billedTurnovers.reduce((sum, t) => {
                                                const b = bookingByTurnoverId[t._id!]
                                                return sum + (b?.cleaning ?? myProperties[t.propertyId].pricing.cleaning ?? 0)
                                            }, 0)}</strong></td>
                                            <td><strong>${billedTurnovers.reduce((sum, t) => {
                                                const b = bookingByTurnoverId[t._id!]
                                                return sum + (b?.source !== 'owner' ? 125 : 0)
                                            }, 0)}</strong></td>
                                            <td><strong>${billedTurnovers.reduce((sum, t) => {
                                                const b = bookingByTurnoverId[t._id!]
                                                const cleaning = b?.cleaning ?? myProperties[t.propertyId].pricing.cleaning ?? 0
                                                const management = b?.source !== 'owner' ? 125 : 0
                                                return sum + cleaning + management
                                            }, 0)}</strong></td>
                                            <td />
                                        </tr>
                                    </tfoot>
                                </Table>
                            )}
                        </>
                    )}

                    {!hasSubmitted && (
                        <Alert variant="secondary">
                            Select an owner and date range, then click Search.
                        </Alert>
                    )}
                </Col>
            </Row>
        </Container>
    )
}
